import { Application } from '@feathersjs/feathers'
import { CreditApplication } from '../../domain/creditApplication'
import { PersonalInFormationFormValues } from './PersonalInformation/types'
import { PromoCodeValues } from './PromoCode/types'
import { Address } from '../../domain/address'
import { AlmostThereFormValues } from './AlmostThere/types'

import {
  setPreviousStep,
  submitCreditApplication,
  validatePromotionCode
} from '../../services/creditApplication.service'
import { FormComponentProps } from '../FormContainer/types'

import AddressFormContainer from './Address/AddressForm.container'
import { PromoCodeContainer } from './PromoCode/PromoCode.container'
import { PersonalInformationContainer } from './PersonalInformation/PersonalInformation.container'
import { AlmostThereContainer } from './AlmostThere/AlmostThere.container'
import { PersonalQuestionValues } from './Address/types'

export type FormState<T> = T

export type handlerFormState =
  | PersonalInFormationFormValues
  | PromoCodeValues
  | PersonalQuestionValues
  | AlmostThereFormValues
  | Placeholder

export type FormComponentType =
  | React.FC<FormComponentProps<PersonalInFormationFormValues>>
  | React.FC<FormComponentProps<PromoCodeValues>>
  | React.FC<FormComponentProps<PersonalQuestionValues>>
  | React.FC<FormComponentProps<AlmostThereFormValues>>
  | React.FC<FormComponentProps<Placeholder>>

type Placeholder = Record<string, unknown | string>

export const TOTAL_STEPS = 7
export interface ApplicationContainerProps {
  creditApplications: CreditApplication[]
  initialState: ApplicationProgress
}

export type ApplicationSubmissionState<handlerFormState> = {
  title?: string
  description?: string
  requiredFields?: string[]
  onSubmitRequest?: (application: Application) => (data?: any) => Promise<void>
  onSubmitApplication?: (
    application: Application
  ) => (payload: SubmissionPayload) => Promise<CreditApplication>
  FormComponent: FormComponentType | null
  readonly step: number
  readonly hasBackTransition: boolean
  formState: handlerFormState
}

export type ApplicationProgress = {
  currentStep: Step
  totalSteps: number
  steps: Step[]
  prevStep: number
}
export interface ApplicationProgressProps {
  handleClick(step: number, updatedStep?: Step): void
}

export interface ContainerStateProps extends ApplicationProgressProps {
  stepState: Step
}

export const ENTER_PROMO: ApplicationSubmissionState<PromoCodeValues> = {
  title: 'Have a promo code?',
  description: 'Enter it here',
  step: 3,
  hasBackTransition: false,
  formState: { promocode: '' },
  requiredFields: ['promocode'],
  onSubmitRequest: validatePromotionCode,
  FormComponent: PromoCodeContainer
}

export const GET_PHYSICAL: ApplicationSubmissionState<PersonalQuestionValues> =
  {
    title: `Let's get physical`,
    description: `A Few Personal Questions`,
    step: 4,
    hasBackTransition: true,
    formState: {
      name: {
        familyName: '',
        givenName: '',
        middleName: ''
      },
      address: {
        street: '',
        city: '',
        state: '',
        postal: '',
        country: 'US',
        refinement: ''
      }
    },
    requiredFields: [
      'address.street',
      'address.city',
      'address.state',
      'address.postal',
      'name.givenName'
    ],
    FormComponent: AddressFormContainer
  }

export const SAFE_AND_SECURE: ApplicationSubmissionState<PersonalInFormationFormValues> =
  {
    title: 'We keep your personal info safe and secure',
    step: 5,
    hasBackTransition: true,
    formState: { dob: null, ssn: '', annualIncome: 0 },
    requiredFields: ['annualIncome', 'ssn', 'dob'],
    FormComponent: PersonalInformationContainer
  }

export const ALMOST_THERE: ApplicationSubmissionState<AlmostThereFormValues> = {
  title: 'Almost There!',
  description: 'Double check that we got all of your information correctly.',
  step: 6,
  hasBackTransition: true,
  formState: {
    promoCode: '',
    legalName: '',
    email: '',
    primaryPhone: '',
    annualIncome: 0,
    address: '',
    dob: '',
    ssn: '',
    kycConfirmation: false
  },
  requiredFields: [
    'legalName',
    'email',
    'primaryPhone',
    'annualIncome',
    'address',
    'dob',
    'ssn',
    'kycConfirmation'
  ],
  onSubmitApplication: submitCreditApplication,
  FormComponent: AlmostThereContainer
}

export type Step =
  | ApplicationSubmissionState<PersonalInFormationFormValues>
  | ApplicationSubmissionState<PromoCodeValues>
  | ApplicationSubmissionState<PersonalQuestionValues>
  | ApplicationSubmissionState<AlmostThereFormValues>
  | ApplicationSubmissionState<Placeholder>

export const STEPS: Step[] = Array.from([
  ENTER_PROMO,
  GET_PHYSICAL,
  SAFE_AND_SECURE,
  ALMOST_THERE
])

export type SubmissionPayload = {
  deviceSessionId?: string
  promoCode?: string
  name: {
    first: string
    middle: string
    last: string
    suffix: string
  }
  address: Address
  birthdate: string
  ssn: string
  income: number
  phone: string
  email: string
  kycConsent: boolean
}

export const APPLICATION_PROGRESS: ApplicationProgress = {
  currentStep: ENTER_PROMO,
  totalSteps: TOTAL_STEPS,
  steps: STEPS,
  prevStep: setPreviousStep(ENTER_PROMO.step)
}

export const IOS_APP_URL = 'https://pacelinefit.app.link/WEB'
